import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Search } from 'lucide-react'
import { initializeMyPartners } from 'slices/myPartnersSlice'
import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar'
import { Button } from 'components/ui/button'
import { Spinner } from 'components/ui/spinner'
import { findPartnerEntry } from 'utils/capabilities'

interface PortfolioAddPartnerPopupProps {
	portfolio: any
	/** Advances to the per-asset access chooser with the chosen MyPartner. */
	onSelectPartner: (partner: any) => void
}

/**
 * Step 1 of adding a portfolio partner: pick someone from the user's existing
 * partner network. Mirrors LocationAddPartnerPopup, except that choosing a row
 * does not dispatch — the parent advances to the access chooser, because a
 * portfolio grant needs a per-asset decision first.
 */
const PortfolioAddPartnerPopup = ({ portfolio, onSelectPartner }: PortfolioAddPartnerPopupProps) => {
	const dispatch = useDispatch<any>()

	const [searchValue, setSearchValue] = useState('')

	const userLogin = useSelector((state: any) => state.userLogin)
	const { userInfo: user } = userLogin

	const { myPartners, isLoading } = useSelector((state: any) => {
		const myPartnerIds = state.myPartners.ids
		return {
			myPartners: myPartnerIds.map((id: string) => state.myPartners.entities[id]),
			isLoading: state.myPartners.isLoading,
		}
	})

	useEffect(() => {
		dispatch(initializeMyPartners())
	}, [dispatch, user])

	const isInPortfolio = (myPartner: any) =>
		Boolean(findPartnerEntry(portfolio, myPartner?.partner?._id))

	const filtered = myPartners
		.filter((partner: any) => partner && !isInPortfolio(partner))
		.filter((partner: any) => {
			if (!searchValue) return true
			const lower = searchValue.toLowerCase()
			return (
				(partner.partner?.name || '').toLowerCase().includes(lower) ||
				(partner.partner?.company || '').toLowerCase().includes(lower) ||
				(partner.partner?.email || '').toLowerCase().includes(lower)
			)
		})

	return (
		<div className="w-full">
			{/* Search */}
			<div className="relative mb-4">
				<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
				<input
					type="text"
					placeholder="Search partners..."
					value={searchValue}
					onChange={event => setSearchValue(event.target.value)}
					className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-10 pr-4 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
				/>
			</div>

			{/* Partners list */}
			{isLoading ? (
				<div className="py-8">
					<Spinner />
				</div>
			) : filtered.length === 0 ? (
				<div className="py-12 text-center text-sm text-gray-400">
					{searchValue ? 'No partners match your search.' : 'No available partners to add.'}
				</div>
			) : (
				<div className="max-h-[45vh] overflow-y-auto divide-y divide-gray-100">
					{filtered.map((partner: any) => (
						<div key={partner.partner._id} className="flex items-center gap-3 py-3">
							<Avatar className="h-9 w-9 flex-shrink-0">
								<AvatarImage src={partner.partner.image} alt={partner.partner.name || ''} />
								<AvatarFallback className="text-sm bg-gray-100 text-gray-600">
									{(partner.partner.name || '?').charAt(0).toUpperCase()}
								</AvatarFallback>
							</Avatar>
							<span className="text-sm font-medium text-gray-900 min-w-0 truncate">
								{partner.partner.name}
							</span>
							<span className="text-sm text-gray-500 min-w-0 truncate">
								{partner.partner.title}
							</span>
							<span className="text-sm text-gray-400 min-w-0 truncate">
								{partner.partner.company}
							</span>
							<div className="ml-auto flex-shrink-0">
								<Button
									variant="primary"
									isOutline
									className="text-xs"
									onClick={() => onSelectPartner(partner)}
								>
									Choose Access
								</Button>
							</div>
						</div>
					))}
				</div>
			)}
		</div>
	)
}

export default PortfolioAddPartnerPopup
